import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a dividend stock screener?',
    a: 'A screener is a filtered list of stocks that match a set of criteria you choose — yield range, payout ratio, dividend frequency, sector, market cap, dividend tier (Kings / Aristocrats / Achievers), etc. It turns a universe of 4,500+ dividend-paying US stocks into a short, qualified candidate list in under a minute.',
  },
  {
    q: 'Why screen instead of just buying SCHD or DGRO?',
    a: 'ETFs are a great default — see the "SCHD vs DGRO" guide for the head-to-head. Screening matters when you want concentrated exposure (a 10-stock starter portfolio), tax-efficient placement (qualified dividends in taxable, REITs in IRAs), or to tilt away from what the popular ETFs hold. The honest answer: 80% of dividend investors should mostly own ETFs and a handful of conviction names found through screening.',
  },
  {
    q: 'What’s the single best filter to start with?',
    a: 'Tier. Set DiviDrip’s Screener to Aristocrats or Kings only — ~65 + ~50 companies respectively. You’ve already filtered out 4,400 of the 4,500 universe with one click and the remaining list is by definition battle-tested. Add a yield filter on top of that to taste.',
  },
  {
    q: 'Should I include high-yielders in my screen?',
    a: 'Cautiously. Anything in the 7–10% band needs sector context (REITs, BDCs, covered-call ETFs can sustain that legitimately). Anything above 11% on a regular C-corporation is almost always a yield trap — see the "Dividend Traps" guide for the 5-point safety check. The Screener’s yield ranges are color-coded by risk level for exactly this reason.',
  },
  {
    q: 'How long should a screen be?',
    a: 'Aim for 10–25 candidates. Fewer than 10 means your filters are too restrictive. More than 25 means too loose to read through carefully. Tighten the strictest filter (payout ratio or tier) first; loosen the broadest (yield range or sector) last.',
  },
  {
    q: 'Does DiviDrip’s Screener save my filters?',
    a: 'Yes. Once you’ve dialled in a filter combination you like, click the share-URL icon next to the filter row — you get a sharable URL that pre-applies your current filter set. Bookmark it, send it to a friend, or save several as different "playbooks" (e.g. a Quality Kings screen, a Monthly Income screen, a Sector Tilt screen).',
  },
];

export default function LearnHowToScreen() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="How to Screen for Dividend Stocks — A Step-by-Step Filter Playbook (2026) | DiviDrip"
      description="A practical step-by-step guide to filtering 4,500+ US dividend stocks down to a short, qualified candidate list. Tier, yield range, payout ratio, sector, frequency — the filters that matter and the order to apply them."
      canonical="https://twylightcrow.com/learn/how-to-screen-for-dividend-stocks"
      guidePath="/learn/how-to-screen-for-dividend-stocks"
      sectionLabel="Practical Workflows"
      sectionAnchor="workflows"
      kicker="Learn · Screening workflow"
      h1="How to Screen for Dividend Stocks — Step by Step"
      datePublished="2026-06-14"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers I check before any buy' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/payout-ratio', label: 'The payout ratio — your first safety check' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — starting with $10K' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Kings vs Aristocrats vs Achievers' },
        { to: '/learn/evaluating-dividend-etfs', label: 'How to evaluate dividend ETFs' },
      ]}
    >
      <p>
        Picking individual dividend stocks from a 4,500-name universe is
        intimidating. A good screener turns it into a 60-second workflow that
        gives you 10&ndash;25 high-quality candidates. This guide walks you
        through DiviDrip&apos;s <Link to="/screener">Screener</Link>{' '}
        one filter at a time, in the order that gets you the cleanest result.
      </p>

      <h2>The 5-filter framework</h2>
      <p>
        Apply these five filters in this order:
      </p>
      <ol>
        <li><strong>Tier</strong> &mdash; remove everything that hasn&apos;t earned its dividend stripes</li>
        <li><strong>Yield range</strong> &mdash; pick your income vs growth balance</li>
        <li><strong>Payout ratio</strong> &mdash; confirm the dividend is affordable</li>
        <li><strong>Sector</strong> &mdash; diversify away from your existing holdings</li>
        <li><strong>Frequency</strong> &mdash; optional, for monthly-income builders</li>
      </ol>
      <p>
        Apply them in that order and you go from 4,500 names to a usable
        shortlist every time. Re-order and the math goes weird (you can finish
        with an empty list because Yield &times; Sector cancelled each other).
      </p>

      <h2>Filter 1 — Tier (start with Aristocrats or Achievers)</h2>
      <p>
        Tier is the strongest single dividend-safety signal. It encodes
        decades of business survival through every recession, war, and oil
        shock. The four DiviDrip tiers:
      </p>
      <ul>
        <li>
          <strong>{'👑'} Kings (50+ years of consecutive raises)</strong> &mdash;
          ~50 companies. Coca-Cola, Johnson &amp; Johnson, Procter &amp; Gamble,
          3M, Colgate-Palmolive. The gold standard. Slow-growing dividends but
          almost unkillable.
        </li>
        <li>
          <strong>{'🏆'} Aristocrats (25+ years)</strong> &mdash; ~65
          companies in the S&amp;P 500. Includes all Kings plus names like
          Walmart, McDonald&apos;s, AbbVie, Cintas, Lowe&apos;s. Battle-tested but
          newer to the elite tier.
        </li>
        <li>
          <strong>{'🎖️'} Achievers (10+ years)</strong> &mdash; a
          much wider list (Apple, Microsoft, Visa, Mastercard, Costco, Texas
          Instruments). Strong growth profile, less history through downturns
          than Aristocrats.
        </li>
        <li>
          <strong>All Tiers</strong> &mdash; the full universe. Use when
          screening for ETFs or for new dividend initiators outside the tiered
          system.
        </li>
      </ul>
      <p>
        For a first portfolio, start with <strong>Aristocrats</strong>. You
        immediately eliminate 99% of the universe and what remains has earned
        the right to be considered.
      </p>

      <h2>Filter 2 — Yield range (match your income goal)</h2>
      <p>
        DiviDrip&apos;s Screener has four color-coded yield bands. Pick the
        one that matches your strategy:
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Band</th><th>Use case</th><th>Examples</th></tr>
        </thead>
        <tbody>
          <tr>
            <td className="text-green-700 dark:text-green-400">0&ndash;3% (Safe)</td>
            <td>Growth-focused. Lower current income, higher dividend-growth-rate.</td>
            <td>Microsoft, Visa, Apple, Costco</td>
          </tr>
          <tr>
            <td className="text-yellow-700 dark:text-yellow-400">4&ndash;6% (Sweet Spot)</td>
            <td>Income-focused. The classic dividend-stock band.</td>
            <td>Verizon, AT&amp;T, big oil majors, regional banks, utilities</td>
          </tr>
          <tr>
            <td className="text-orange-700 dark:text-orange-400">7&ndash;10% (Warning)</td>
            <td>High income. Requires sector context. Yield-trap risk.</td>
            <td>BDCs (ARCC, MAIN), covered-call ETFs (JEPI, JEPQ), mortgage REITs</td>
          </tr>
          <tr>
            <td className="text-red-700 dark:text-red-400">11%+ (Danger)</td>
            <td>Almost always a cut warning for traditional stocks. Open for special structures.</td>
            <td>Distressed equities, some mREITs, leveraged income ETFs</td>
          </tr>
        </tbody>
      </table>
      <p>
        A balanced dividend portfolio usually pulls 60&ndash;70% from the
        sweet-spot band, 20&ndash;30% from the safe band, and a small allocation
        to higher-yielders if at all.
      </p>

      <h2>Filter 3 — Payout ratio (the safety check)</h2>
      <p>
        After tier and yield, payout ratio is your one-glance dividend-safety
        gate. The DiviDrip Screener has four ranges:
      </p>
      <ul>
        <li><strong>Under 60% (Safe)</strong> &mdash; comfortable cushion, room for raises.</li>
        <li><strong>60&ndash;80% (Moderate)</strong> &mdash; manageable but watch trend.</li>
        <li><strong>81&ndash;100% (High)</strong> &mdash; little margin for error.</li>
        <li><strong>Above 100% (Unsustainable)</strong> &mdash; cut watch.</li>
      </ul>
      <p>
        For a Kings/Aristocrats screen, layer in the &lt;60% filter and your
        list shrinks to the long-term safest names. For higher-yield REITs and
        BDCs, the &lt;60% filter is the wrong question — use the color
        coding inside the Stock Modal’s Dividend Info tab which adjusts for
        the sector’s AFFO/NII coverage instead. See{' '}
        <Link to="/learn/payout-ratio">the payout ratio guide</Link>.
      </p>

      <h2>Filter 4 — Sector (diversify away from what you own)</h2>
      <p>
        Open your existing portfolio in DiviDrip first — the sector pie
        chart on the dashboard shows where you&apos;re already heavy. Then use
        the Screener’s Sector filter to find names in your
        <em> under-represented</em> sectors. Common gaps for new dividend
        investors:
      </p>
      <ul>
        <li><strong>Utilities</strong> — stable, regulated, classic &quot;sleep well&quot; sector. Often missing.</li>
        <li><strong>Consumer Defensive</strong> — where many Kings live. Reliable cash flow through recessions.</li>
        <li><strong>Healthcare</strong> — demographically tailwind for decades. JNJ, ABBV, MRK, PFE.</li>
        <li><strong>Real Estate (REITs)</strong> — higher yields, inflation-resistant rents.</li>
      </ul>
      <p>
        Apply one under-represented sector at a time and you build a balanced
        portfolio over several screening sessions.
      </p>

      <h2>Filter 5 — Frequency (optional: monthly income building)</h2>
      <p>
        If you&apos;re building a monthly cash-flow portfolio, the Frequency
        filter lets you isolate the small but growing pool of monthly payers.
        About 25 stocks and 40+ ETFs pay monthly in the US (Realty Income,
        Main Street Capital, AGNC, STAG, the JEPI/JEPQ family). See{' '}
        <Link to="/learn/monthly-dividend-calendar">the monthly dividend
        calendar trick</Link> for how to combine monthly payers with quarterly
        ones for weekly cash flow.
      </p>
      <p>
        For most accumulation-phase investors, leave Frequency on &quot;All
        Frequencies&quot; — the discipline of waiting three months between
        payments doesn&apos;t hurt your math.
      </p>

      <h2>Worked example: a $25K starter screen</h2>
      <p>
        Goal: balanced dividend portfolio, 3&ndash;5% blended yield, defensive
        sectors, monthly cash flow optional.
      </p>
      <ol>
        <li>Tier: <strong>Aristocrats</strong> — ~65 candidates remaining.</li>
        <li>Yield Range: <strong>4&ndash;6% (Sweet Spot)</strong> — ~20 candidates remaining.</li>
        <li>Payout Ratio: <strong>under 80%</strong> — ~15 candidates remaining.</li>
        <li>Sector: <strong>Consumer Defensive + Utilities + Healthcare</strong> in three separate runs — 5&ndash;7 candidates each pass.</li>
        <li>Click each candidate to open the Stock Modal, check the Triangle Score on the Triangle tab, and look at the Streak Risk Chart.</li>
      </ol>
      <p>
        You finish with maybe 8&ndash;12 names that have all earned the right to
        be in a long-term portfolio. Sort them by Triangle Score and the top
        4&ndash;6 become your initial buy list.
      </p>

      <h2>Saving and sharing your screens</h2>
      <p>
        DiviDrip&apos;s Screener has a sharable-URL button (next to the active
        filter chips) that captures your full filter state. Click it, copy the
        link, and bookmark it as your &quot;Quality Aristocrats&quot; or
        &quot;Monthly Income&quot; or &quot;Healthcare Tilt&quot; screen.
        Re-applying takes one click instead of re-clicking five dropdowns.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open the <Link to="/screener">DiviDrip Screener</Link> in a new tab,
        set Tier to <strong>Aristocrats</strong> and Yield to <strong>4&ndash;6%
        (Sweet Spot)</strong>, and you&apos;ll have a ~20-name shortlist in a
        single click. Click any ticker to open the Stock Modal and read the
        Triangle Score — you&apos;ll have a defensible buy candidate inside
        of two minutes.
      </p>
    </LearnPage>
  );
}
